import type { Target } from 'src/models/TimerTarget';

import { PlayIcon, StopIcon } from '@phosphor-icons/react';
import { css, html } from 'react-strict-dom';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { useTimer, useTimerElapsed } from 'src/contexts';
import { Model } from 'src/models';
import { formatElapsed } from 'src/util';

import Text from './Text';

type Size = 'small' | 'large';

type Props = {
  /** What the timer runs against.  The button tracks it in the global timer. */
  target: Target;
  /** Sent with a start, where the target takes a category. */
  category?: string | null;
  /** 28px in list rows and the side menu, 40px in the dashboard module. */
  size?: Size;
  /** Solid where starting is the one thing to do, subtle everywhere else. */
  tone?: 'solid' | 'subtle';
  /** Shows "Start" when stopped and the running clock when it is going. */
  showLabel?: boolean;
  /** Overrides the stopped label, e.g. `Start #607`. */
  startLabel?: string;
  /** Held shut for the caller's own reasons, e.g. no category picked yet. */
  disabled?: boolean;
};

/**
 * The start/stop control.  Drop it on anything time can be logged against — a
 * ticket, a project, a service order, a client — and it works out from the
 * global timer whether it is the one running.
 */
export default function TimerButton({
  target,
  category = null,
  size = 'small',
  tone = 'subtle',
  showLabel = false,
  startLabel = 'Start',
  disabled = false,
}: Props) {
  const timer = useTimer();
  const running = timer.isRunning(target);

  // A start the API would refuse for want of a category is held shut rather
  // than left to fail on the click.  Stopping is never held back.
  const needsCategory =
    !running && Model.TimerTarget.isCategoryRequired(target) && !category;
  const held = disabled || needsCategory || timer.isBusy;

  const solid = tone === 'solid';
  const large = size === 'large';

  return (
    <html.button
      style={[
        Style.row,
        Style.centerBoth,
        styles.button,
        large ? styles.large : styles.small,
        showLabel
          ? large
            ? styles.largeLabel
            : styles.smallLabel
          : large
          ? styles.largeIcon
          : styles.smallIcon,
        running
          ? solid
            ? styles.runningSolid
            : styles.runningSubtle
          : solid
          ? styles.stoppedSolid
          : styles.stoppedSubtle,
        held && styles.held,
      ]}
      disabled={held}
      aria-label={`${running ? 'Stop' : 'Start'} timer on ${target.title}`}
      onClick={
        held
          ? undefined
          : () => (running ? timer.stop() : timer.start(target, category))
      }
    >
      {running ? (
        <StopIcon size={12} weight="fill" color={Color.neutral80} />
      ) : (
        <PlayIcon
          size={large ? 13 : 12}
          weight="fill"
          color={solid ? Color.neutral0 : Color.green}
        />
      )}

      {showLabel &&
        (running ? (
          <ElapsedLabel size={size} />
        ) : (
          <Text
            typeface={large ? 'body.small.medium' : 'caption.medium'}
            color={solid ? 'neutral0' : 'green'}
          >
            {startLabel}
          </Text>
        ))}
    </html.button>
  );
}

/**
 * Split out so the buttons without a clock on them stay off the tick.
 */
function ElapsedLabel({ size }: { size: Size }) {
  const elapsedSeconds = useTimerElapsed();

  return (
    <Text
      typeface={size === 'large' ? 'body.small.medium' : 'caption.medium'}
      color="neutral100"
      style={styles.elapsed}
    >
      {formatElapsed(elapsedSeconds)}
    </Text>
  );
}

const styles = css.create({
  button: {
    flexShrink: 0,
    borderWidth: 0,
    borderRadius: 999,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  small: {
    height: 28,
    gap: 6,
  },
  large: {
    height: 40,
    gap: 8,
  },
  smallIcon: {
    width: 28,
  },
  largeIcon: {
    width: 40,
  },
  smallLabel: {
    paddingLeft: 8,
    paddingRight: 10,
  },
  largeLabel: {
    paddingLeft: 14,
    paddingRight: 16,
    // A labelled large button is a pill rather than a circle.
    borderRadius: 8,
  },
  stoppedSolid: {
    backgroundColor: color.green,
  },
  stoppedSubtle: {
    backgroundColor: color.greenBg,
  },
  // Stopping has nothing to sell, so running drops the green entirely.
  runningSolid: {
    backgroundColor: color.neutral10,
  },
  runningSubtle: {
    backgroundColor: color.neutral0,
  },
  held: {
    opacity: 0.5,
    cursor: 'default',
  },
  elapsed: {
    // Keeps the clock from jittering as the digits change under it.
    fontVariantNumeric: 'tabular-nums',
  },
});
